import type { Places } from '~/libs/dato/queries/places';
import type { JourneyTransformed } from '~/types/Journey.types';
import type { StayTransformed } from '~/types/JourneyItem.types';

export type PlaceImage = {
  placeName: string;
  responsiveImage: {
    src: string;
    width: number;
    height: number;
    alt: string | null;
    title: string | null;
    aspectRatio: number;
    bgColor: string | null;
  } | null;
};

/**
 * Resolve CMS place images for a trip's stays by matching place name.
 *
 * Stays are sorted longest-first and de-duplicated so the most significant
 * place leads. Images come entirely from the playground store's already-loaded
 * cmsPlaces — no server round-trip. Shared by TripImageCarousel (full set) and
 * TripCard (first image as thumbnail).
 */
export function getStayPlaceImages(stays: StayTransformed[], cmsPlaces: Places['allPlaces']): PlaceImage[] {
  // Sort stays by nights (longest first), then by order for tie-breaking
  const sortedStays = [...stays].sort((a, b) => b.nights - a.nights);

  // Deduplicate places - first occurrence only
  const seenPlaces = new Set<string>();
  const uniqueStays = sortedStays.filter((stay) => {
    if (seenPlaces.has(stay.title)) {
      return false;
    }
    seenPlaces.add(stay.title);
    return true;
  });

  // Match stays to CMS place images
  return uniqueStays
    .flatMap((stay) => {
      const cmsPlace = cmsPlaces.find((place) => place.placeName === stay.title);
      if (!cmsPlace?.images || cmsPlace.images.length === 0) return [];
      return cmsPlace.images.map((image) => ({
        placeName: cmsPlace.placeName,
        responsiveImage: image.responsiveImage
      }));
    })
    .filter((image): image is PlaceImage => image?.responsiveImage !== null && image?.responsiveImage !== undefined);
}

/**
 * Pick one thumbnail per trip for the trip list, avoiding repeats across cards.
 *
 * Each trip claims the first of its candidate place images (longest stay first)
 * whose src hasn't already been used by an earlier trip — so two trips sharing a
 * dominant place don't show the same photo. Falls back to a trip's first image
 * when all its candidates are taken. Mirrors the original header_image dedup that
 * the maps skeleton path lost when header images moved to CMS-only.
 */
export function getDistinctTripThumbnails(
  trips: JourneyTransformed[],
  cmsPlaces: Places['allPlaces']
): (PlaceImage['responsiveImage'] | null)[] {
  const usedSrcs = new Set<string>();

  return trips.map((trip) => {
    const stays = trip.items.filter((item): item is StayTransformed => item.type === 'Stay');
    const candidates = getStayPlaceImages(stays, cmsPlaces);
    const pick =
      candidates.find((image) => image.responsiveImage?.src && !usedSrcs.has(image.responsiveImage.src)) ??
      candidates[0];

    if (pick?.responsiveImage?.src) {
      usedSrcs.add(pick.responsiveImage.src);
    }
    return pick?.responsiveImage ?? null;
  });
}
